import type { IDropLocation } from "../store/useDropLocations";

const CITY_PRIORITY = ["Amsterdam", "Utrecht", "Den Haag", "Rotterdam"];
const LOCATION_PRIORITY_ORDER = [
  "VLS",
  "MAR",
  "BOLO",
  "BSM",
  "SMA",
  "EDP",
  "FHS",
  "SLP",
  "VBS",
  "TRS",
  "LDS",
];
const LOCATION_PRIORITY_MAP = LOCATION_PRIORITY_ORDER.reduce<
  Record<string, number>
>((acc, code, index) => {
  acc[code] = index;
  return acc;
}, {});
const LOCATION_CODE_ALIASES: Record<string, string> = {
  DSL: "SLP",
  VS: "VBS",
};

const CITY_PRIORITY_NORMALIZED = CITY_PRIORITY.map((city) =>
  city.toLowerCase()
);

const normalizeCity = (city?: string | null) =>
  city?.trim().toLowerCase() ?? "";

const getCityRank = (city?: string | null) => {
  const normalized = normalizeCity(city);
  const priorityIndex = CITY_PRIORITY_NORMALIZED.indexOf(normalized);
  return priorityIndex === -1 ? CITY_PRIORITY.length : priorityIndex;
};

const getOpeningTimestamp = (
  openingDate?: string | Date | null,
  createdAt?: string | Date | null
) => {
  const attempt = openingDate ?? createdAt;
  if (!attempt) return Number.POSITIVE_INFINITY;
  const timestamp = new Date(attempt).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

const buildLocationMeta = (dropLocations?: IDropLocation[] | null) => {
  const meta: Record<
    string,
    {
      cityRank: number;
      openingTime: number;
    }
  > = {};

  const addEntry = (
    key: string | undefined | null,
    info: { cityRank: number; openingTime: number }
  ) => {
    if (!key) return;
    const trimmed = key.trim();
    if (!trimmed) return;
    meta[trimmed] = info;
    meta[trimmed.toLowerCase()] = info;
  };

  const extractShortCodeFromCostPlace = (costPlace?: string | null) => {
    if (!costPlace) return undefined;
    const tokens = costPlace.split("_");
    return tokens.find((token) => /^[A-Z]{3}$/.test(token));
  };

  const buildDisplayLabel = (
    shortCode?: string,
    conceptType?: string | null
  ) => {
    if (!shortCode) return undefined;
    const concept = conceptType?.toUpperCase();
    return concept ? `${shortCode} (${concept})` : shortCode;
  };

  dropLocations?.forEach((location) => {
    if (!location) return;
    const info = {
      cityRank: getCityRank(location.city),
      openingTime: getOpeningTimestamp(
        location.openingDate,
        location.createdAt
      ),
    };

    addEntry(location.cost_place, info);
    addEntry(location.name, info);
    addEntry(location.city, info);
    addEntry(location._id?.toString(), info);

    const shortCode = extractShortCodeFromCostPlace(location.cost_place);
    const displayLabel = buildDisplayLabel(shortCode, location.conceptType);

    addEntry(shortCode, info);
    addEntry(displayLabel, info);
  });

  return meta;
};

const extractShortCodeFromLabel = (value?: string | null) => {
  if (!value) return undefined;
  const tokens = value
    .toUpperCase()
    .replace(/[^A-Z]/g, " ")
    .split(" ")
    .filter(Boolean);

  for (const token of tokens) {
    if (token.length !== 3 && token.length !== 4) continue;
    const normalized = LOCATION_CODE_ALIASES[token] ?? token;
    // Check if normalized code exists in priority map or order array
    if (
      LOCATION_PRIORITY_MAP.hasOwnProperty(normalized) ||
      LOCATION_PRIORITY_ORDER.includes(normalized)
    ) {
      return normalized;
    }
  }
  return undefined;
};

const getLocationPriority = (value: string) => {
  const code = extractShortCodeFromLabel(value);
  if (!code) return undefined;
  // Check priority map first (for codes in map)
  if (LOCATION_PRIORITY_MAP.hasOwnProperty(code)) {
    return LOCATION_PRIORITY_MAP[code];
  }
  // For codes not in map (like 4-char codes), check LOCATION_PRIORITY_ORDER directly
  const index = LOCATION_PRIORITY_ORDER.indexOf(code);
  return index !== -1 ? index : undefined;
};

const compareByLocation = (
  nameA: string,
  nameB: string,
  meta: ReturnType<typeof buildLocationMeta>,
  fallbackOrder: Map<string, number>
) => {
  if (nameA === nameB) return 0;

  const priorityA = getLocationPriority(nameA);
  const priorityB = getLocationPriority(nameB);
  if (priorityA !== undefined || priorityB !== undefined) {
    if (priorityA === undefined) return 1;
    if (priorityB === undefined) return -1;
    if (priorityA !== priorityB) return priorityA - priorityB;
  }

  const resolveMeta = (value: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    const lowered = trimmed.toLowerCase();
    return meta[value] ?? meta[trimmed] ?? meta[lowered];
  };

  const metaA = resolveMeta(nameA);
  const metaB = resolveMeta(nameB);

  const rankA = metaA?.cityRank ?? CITY_PRIORITY.length;
  const rankB = metaB?.cityRank ?? CITY_PRIORITY.length;
  if (rankA !== rankB) return rankA - rankB;

  const openA = metaA?.openingTime ?? Number.POSITIVE_INFINITY;
  const openB = metaB?.openingTime ?? Number.POSITIVE_INFINITY;
  if (openA !== openB) return openA - openB;

  return (fallbackOrder.get(nameA) ?? 0) - (fallbackOrder.get(nameB) ?? 0);
};

export const sortLocationNames = (
  locations: string[],
  dropLocations?: IDropLocation[] | null
) => {
  const meta = buildLocationMeta(dropLocations);
  const fallbackOrder = new Map<string, number>();
  locations.forEach((name, index) => fallbackOrder.set(name, index));

  return [...locations].sort((a, b) =>
    compareByLocation(a, b, meta, fallbackOrder)
  );
};

export const sortSeriesByLocation = <T extends { name?: string }>(
  series: T[],
  dropLocations?: IDropLocation[] | null
) => {
  const meta = buildLocationMeta(dropLocations);
  const fallbackOrder = new Map<string, number>();
  series.forEach((item, index) => fallbackOrder.set(item?.name ?? "", index));

  return [...series].sort((a, b) =>
    compareByLocation(a?.name ?? "", b?.name ?? "", meta, fallbackOrder)
  );
};

/** Get chart labels in the same order as filterPlaces (for user-defined ordering) */
export const getOrderedLabelsFromFilterPlaces = (
  filterPlaces: string[],
  filterType: "location" | "city",
  dropLocations?: IDropLocation[] | null,
  totalAllStoresId = "__TOTAL_ALL_STORES__"
): string[] => {
  const realFilterPlaces = filterPlaces.filter((p) => p !== totalAllStoresId);
  if (realFilterPlaces.length === 0) return [];

  const labels: string[] = [];
  for (const id of realFilterPlaces) {
    if (filterType === "city") {
      labels.push(id);
    } else {
      const loc = dropLocations?.find((l) => l._id === id || l.cost_place === id);
      if (loc) {
        const short =
          (loc.cost_place ?? "")
            .split("_")
            ?.find((t) => /^[A-Z]{3,4}$/.test(t)) ?? loc.cost_place ?? "";
        const concept = loc.conceptType?.toString().toUpperCase() ?? "";
        labels.push(concept ? `${short} (${concept})` : short || id);
      }
    }
  }
  return labels;
};

export const sortSummaryStatsByLocation = <
  T extends { location?: { cost_place?: string } },
>(
  stats: T[],
  dropLocations?: IDropLocation[] | null
) => {
  const meta = buildLocationMeta(dropLocations);
  const fallbackOrder = new Map<string, number>();
  stats.forEach((item, index) =>
    fallbackOrder.set(item?.location?.cost_place ?? "", index)
  );

  return [...stats].sort((a, b) =>
    compareByLocation(
      a?.location?.cost_place ?? "",
      b?.location?.cost_place ?? "",
      meta,
      fallbackOrder
    )
  );
};
